import { ColumnWrapper } from '@components/BookingDetails/BookingDetails.styles';
import {
  LuxAccordion,
  LuxButton,
  LuxDivider,
  LuxModal,
  LuxModalHeader,
  LuxTabsGroup,
  LuxTypography,
  Stack,
  useMediaQuery,
  UserDoctorIcon,
  useTheme,
} from '@gemini/fe-ds-core';
import useDoctors from '@hooks/useDoctors';
import useGetAppointments from '@hooks/useGetAppointments';
import useGetPaymentMethods from '@hooks/useGetPaymentMethods';
import {
  OnlineBookingStore,
  useOnlineBookingStore,
} from '@store/onlineBookingStore';
import { GeneralInfoDTO } from '@utils/interfaces';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CustMessContainer,
  CustomMessageText,
  CustomMessageTextBox,
  DoctorAvatarInfoContainer,
  DoctorInfoContainer,
  ElementsList,
  InfoRowContainer,
  ListElement,
  ModalContentWrapper,
  MoreInfoAccordionContent,
} from './MoreInformationModal.styles';
import useGetInsuranceConfiguration from '@hooks/useGetInsuranceConfiguration';
import DataGeneralTable from '@components/DataGeneralTable/DataGeneralTable';
import {
  MobileBreakpoint,
  TabletBreakpoint,
} from '@utils/consts/breakpoints.consts';
import dayjs, { Dayjs } from 'dayjs';
import { useFocusTrap } from '@hooks/useFocusTrap';
import {
  formatGeneralAddress,
  phoneLink,
} from '@components/BookingDetails/utils';
import { guardNoopClickableElement } from '@hooks/useNoopClickableGuard';
import { createPortal } from 'react-dom';

interface MoreInfosModalProps {
  generalData?: GeneralInfoDTO;
  open: boolean;
  onClose: () => void;
  fromViewDoctors?: boolean;
  onlineBookingStore?: OnlineBookingStore;
}

const MoreInformationModal = ({
  generalData,
  open,
  onClose,
  fromViewDoctors = false,
  onlineBookingStore,
}: MoreInfosModalProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  useFocusTrap({
    isOpen: open,
    onClose,
    selector: '[id^="LuxDrawerResizable"]',
  });

  const isMobile = useMediaQuery(`(max-width:${TabletBreakpoint}px)`);
  const examServices = useOnlineBookingStore(
    state => state.generalInfo?.examService,
  );
  const practiceInformation =
    useOnlineBookingStore(state => state.generalConfig?.practiceInformation) ||
    onlineBookingStore?.generalConfig?.practiceInformation;
  const practiceId =
    useOnlineBookingStore(state => state.practiceId) ||
    onlineBookingStore?.practiceId ||
    '';
  const organizationId =
    useOnlineBookingStore(state => state.generalInfo?.organizationId) ||
    onlineBookingStore?.generalInfo?.organizationId;

  const brandName = useOnlineBookingStore(state => state.brandName);

  const { data: acceptedInsurances } = useGetInsuranceConfiguration({
    organizationId: brandName ? practiceId : organizationId,
  });
  const { data: appointments } = useGetAppointments({
    practiceIdValue: practiceId,
  });
  const { data: payments } = useGetPaymentMethods({ practiceId });
  const { data: doctors } = useDoctors({
    practiceId,
    enabled: true,
  });

  const [breakpoint, setBreakpoint] = useState<'mobile' | 'desktop'>(
    window.innerWidth > MobileBreakpoint ? 'desktop' : 'mobile',
  );
  const [isBottomModal, setIsBottomModal] = useState<boolean>(
    window.innerWidth > TabletBreakpoint,
  );

  useEffect(() => {
    if (!open) return;

    const drawer = document.querySelector<HTMLElement>(
      '[id^="LuxDrawerResizable"]',
    );

    drawer
      ?.querySelectorAll<HTMLElement>('div[draggable="true"]')
      .forEach(el => {
        el.setAttribute('aria-hidden', 'true');

        if (el.parentElement) guardNoopClickableElement(el.parentElement);
      });
  }, [open]);

  // ── expand/collapse state per ogni sezione ────────────────────────────────
  const [showAllExams, setShowAllExams] = useState(false);
  const [showAllPayments, setShowAllPayments] = useState(false);
  const [showAllInsurances, setShowAllInsurances] = useState(false);

  const COLLAPSE_THRESHOLD = 10;

  const fees = useMemo(() => {
    if (practiceInformation?.fees) return practiceInformation.fees;
    return undefined;
  }, [practiceInformation?.fees]);

  const payMethods = useMemo(() => {
    return payments && payments.method_list
      ? payments.method_list.split(',')
      : [];
  }, [payments]);

  const days = useMemo(
    () => [
      {
        label: t('moreInfosModal.days.mon'),
        id: 'MON',
      },
      {
        label: t('moreInfosModal.days.tue'),
        id: 'TUE',
      },
      {
        label: t('moreInfosModal.days.wed'),
        id: 'WED',
      },
      {
        label: t('moreInfosModal.days.thu'),
        id: 'THU',
      },
      {
        label: t('moreInfosModal.days.fri'),
        id: 'FRI',
      },
      {
        label: t('moreInfosModal.days.sat'),
        id: 'SAT',
      },
      {
        label: t('moreInfosModal.days.sun'),
        id: 'SUN',
      },
    ],
    [t],
  );

  const getDayJsTime = useCallback((hour: string) => {
    if (hour.indexOf(':') === -1) return undefined;

    const splitOpeningTime = hour.split(':');

    if (splitOpeningTime.length === 0) return undefined;

    const hourOpeningTime = parseInt(splitOpeningTime[0]);
    const minuteOpeningTime = parseInt(
      splitOpeningTime.length > 2 ? splitOpeningTime[1] : '0',
    );

    if (isNaN(hourOpeningTime) || isNaN(minuteOpeningTime)) return undefined;

    return dayjs()
      .set('hour', hourOpeningTime)
      .set('minute', minuteOpeningTime);
  }, []);

  const practiceHours = useMemo(() => {
    const slots: Map<string, Array<Dayjs>> = new Map();
    if (days && Array.isArray(generalData?.hours)) {
      // get All hours
      generalData.hours.forEach(hour => {
        if (Array.isArray(hour.day)) {
          // for each hour get opening time and closing time
          const openingTime = getDayJsTime(hour.openingTime);
          const closingTime = getDayJsTime(hour.closingTime);
          if (openingTime && closingTime) {
            // for each day add opening time and closing time and sort it
            hour.day.forEach(day => {
              const slot = slots.get(day);
              if (slot) {
                slot.push(openingTime);
                slot.push(closingTime);
                if (slot.length > 2) {
                  // sort only if more than 2
                  slot.sort((a, b) => a.valueOf() - b.valueOf());
                }
              } else {
                slots.set(day, [openingTime, closingTime]);
              }
            });
          }
        }
      });
    }
    return slots;
  }, [days, generalData, t]);

  const [selectedTab, setSelectedTab] = useState<any>();

  const sections = useMemo(() => {
    const tempSec = [];
    if (
      practiceInformation?.showExamServices &&
      ((examServices && examServices.length > 0) ||
        (appointments && appointments.length > 0))
    ) {
      tempSec.push({
        index: tempSec.length,
        content: t('moreInfosModal.examServices'),
        id: 'exams',
      });
    }
    if (
      (practiceInformation?.showPaymentOptions &&
        payMethods &&
        payMethods.length > 0) ||
      (practiceInformation?.showAcceptedInsurances &&
        acceptedInsurances &&
        acceptedInsurances?.length > 0) ||
      (practiceInformation?.showFees && !!fees)
    ) {
      tempSec.push({
        index: tempSec.length,
        content: t('moreInfosModal.paymentInfo'),
        id: 'payments',
      });
    }
    if (doctors && doctors.length > 0) {
      tempSec.push({
        index: tempSec.length,
        content: t('moreInfosModal.doctorInfo'),
        id: 'doctors',
      });
    }
    return tempSec;
  }, [
    practiceInformation,
    examServices,
    t,
    acceptedInsurances,
    doctors,
    payMethods,
    fees,
    appointments,
  ]);

  const visibleExamServices = useMemo(() => {
    if (!examServices?.length) return [];

    return showAllExams
      ? examServices
      : examServices.slice(0, COLLAPSE_THRESHOLD);
  }, [examServices, showAllExams]);

  const visibleAppointments = useMemo(() => {
    if (!appointments?.length) return [];

    return showAllExams
      ? appointments
      : appointments.slice(0, COLLAPSE_THRESHOLD);
  }, [appointments, showAllExams]);

  useEffect(() => {
    if (sections) {
      if (fromViewDoctors)
        setSelectedTab(sections.find(sec => sec.id === 'doctors'));
      else setSelectedTab(sections[0]);
    }
  }, [sections, fromViewDoctors]);

  window.addEventListener('resize', () => {
    if (breakpoint === 'mobile' && window.innerWidth > MobileBreakpoint)
      setBreakpoint('desktop');
    if (breakpoint === 'desktop' && window.innerWidth <= MobileBreakpoint)
      setBreakpoint('mobile');
    if (window.innerWidth > TabletBreakpoint) {
      setIsBottomModal(true);
    } else {
      setIsBottomModal(false);
    }
  });

  return open
    ? createPortal(
        <LuxModal
          type={isBottomModal ? 'right' : 'bottom'}
          open={open}
          minWidthPerc={60}
          maxHeightPerc={85}
          onClose={onClose}
          resizable
          header={
            <LuxModalHeader
              darkMode={false}
              enableCloseIcon
              onClose={onClose}
              headerSx={{
                '& > .MuiBox-root': {
                  justifyContent: 'space-between',
                  width: '100%',
                },
              }}
            >
              <ColumnWrapper>
                <LuxTypography
                  variant="heading3"
                  component="h2"
                  sx={{
                    fontSize: `${theme.palette.LUX.obTokens.textH2MobSize} !important`,
                    lineHeight: `${theme.palette.LUX.obTokens.textH2MobLineHeight} !important`,
                    textTransform: 'uppercase !important',
                  }}
                >
                  {generalData?.name}
                </LuxTypography>
                <LuxTypography variant="textMdRegular">
                  {formatGeneralAddress(generalData, !!brandName)}
                </LuxTypography>
                <LuxTypography
                  variant="textMdRegular"
                  fontWeight={breakpoint === 'mobile' ? '700' : ''}
                >
                  {breakpoint === 'mobile'
                    ? phoneLink(generalData?.phoneNumber)
                    : generalData?.phoneNumber}
                </LuxTypography>
              </ColumnWrapper>
            </LuxModalHeader>
          }
        >
          <ModalContentWrapper
            style={{ paddingBottom: isMobile ? '1.75rem' : '7rem' }}
          >
            {practiceInformation?.showPracticeHours &&
              practiceHours.size > 0 && (
                <>
                  <LuxTypography variant="heading4" component="h3">
                    {t('moreInfosModal.hours')}
                  </LuxTypography>
                  {breakpoint === 'desktop' ? (
                    <DataGeneralTable
                      rows={[
                        [
                          ...days.map(d => (
                            <LuxTypography key={d.id} variant="textMedium">
                              {d.label}
                            </LuxTypography>
                          )),
                        ],
                        [
                          ...days.map(d => {
                            const hours = practiceHours.get(d.id);
                            if (hours) {
                              return (
                                <Stack
                                  direction="column"
                                  key={`hours-${d.id}`}
                                  gap={'0.5rem'}
                                  style={{
                                    borderRadius: 'unset',
                                    backgroundColor: 'unset',
                                    border: 'unset',
                                  }}
                                >
                                  <LuxTypography variant="textXs">
                                    {hours[0].format('hh:mm A')}
                                  </LuxTypography>
                                  <LuxTypography variant="textXs">
                                    {hours[1].format('hh:mm A')}
                                  </LuxTypography>
                                  {hours.length > 2 && (
                                    <>
                                      <LuxDivider />
                                      <LuxTypography variant="textXs">
                                        {hours[2].format('hh:mm A')}
                                      </LuxTypography>
                                      <LuxTypography variant="textXs">
                                        {hours[3].format('hh:mm A')}
                                      </LuxTypography>
                                    </>
                                  )}
                                </Stack>
                              );
                            }

                            return t('moreInfosModal.closed');
                          }),
                        ],
                      ]}
                      rowFill="even"
                      columnsTemplate="repeat(7, 1fr)"
                      firstColumnAsHeader={false}
                      firstRowAsHeader
                    />
                  ) : (
                    <DataGeneralTable
                      rows={days.map(d => {
                        const hours = practiceHours.get(d.id);
                        let hoursText = t('moreInfosModal.closed');
                        if (hours) {
                          hoursText = `${hours[0].format('hh:mm A')} - ${hours[1].format('hh:mm A')}`;
                          if (hours.length > 2) {
                            hoursText += ` / ${hours[2].format('hh:mm A')} - ${hours[3].format('hh:mm A')}`;
                          }
                        }
                        return [
                          <LuxTypography key={d.id} variant="textMedium">
                            {d.label}
                          </LuxTypography>,
                          hoursText,
                        ];
                      })}
                      rowFill="odd"
                      columnsTemplate="1fr 5fr"
                    />
                  )}
                </>
              )}
            {sections.length > 0 && (
              <LuxTabsGroup
                tabs={sections}
                onSelect={index => {
                  setSelectedTab(
                    sections.find(sec => sec.index === index) ?? sections[0],
                  );
                }}
                showIcon={false}
                selected={selectedTab?.index ?? 0}
                disableScrollable
                noLabel
                sx={{
                  ...(breakpoint === 'mobile' && {
                    '& .lux-tabs-group': {
                      height: 'unset',
                      '& button': {
                        flex: '1 1 0',
                        height: '100%',
                      },
                    },
                  }),
                }}
              />
            )}

            {/* ── Exam services tab ─────────────────────────────────────── */}
            {selectedTab?.id === 'exams' && (
              <LuxAccordion
                title={
                  <LuxTypography
                    variant="heading4"
                    component="h3"
                    padding={'0.5rem 0'}
                  >
                    {t('moreInfosModal.examAccordion')}
                  </LuxTypography>
                }
                fullWidthContent
                defaultExpanded
              >
                <MoreInfoAccordionContent>
                  <LuxTypography variant="textMdRegular">
                    {t('moreInfosModal.examDisclaimer')}
                  </LuxTypography>
                  <LuxTypography variant="textMdRegular">
                    <ElementsList isMobile={breakpoint === 'mobile'}>
                      {visibleExamServices.length > 0
                        ? visibleExamServices.map(service => (
                            <ListElement key={service}>
                              <LuxTypography variant="textMdRegular">
                                {service}
                              </LuxTypography>
                            </ListElement>
                          ))
                        : visibleAppointments.length > 0
                          ? visibleAppointments.map(a => (
                              <ListElement key={a.id}>
                                <LuxTypography variant="textMdRegular">
                                  {a.title}
                                </LuxTypography>
                              </ListElement>
                            ))
                          : null}
                    </ElementsList>
                  </LuxTypography>
                  {((examServices &&
                    examServices.length > COLLAPSE_THRESHOLD) ||
                    (appointments &&
                      appointments.length > COLLAPSE_THRESHOLD)) && (
                    <LuxButton
                      variant="link"
                      onClick={() => setShowAllExams(prev => !prev)}
                    >
                      {showAllExams
                        ? t('moreInfosModal.showLess')
                        : t('moreInfosModal.showAll')}
                    </LuxButton>
                  )}
                </MoreInfoAccordionContent>
              </LuxAccordion>
            )}

            {/* ── Payments tab ──────────────────────────────────────────── */}
            {selectedTab?.id === 'payments' && (
              <>
                {practiceInformation?.showPaymentOptions &&
                  payMethods &&
                  payMethods.length > 0 && (
                    <LuxAccordion
                      title={
                        <LuxTypography
                          variant="heading4"
                          component="h3"
                          padding={'0.5rem 0'}
                        >
                          {t('moreInfosModal.paymentAccordion')}
                        </LuxTypography>
                      }
                      fullWidthContent
                      defaultExpanded
                    >
                      <MoreInfoAccordionContent>
                        <ElementsList isMobile={breakpoint === 'mobile'}>
                          {(showAllPayments
                            ? payMethods
                            : payMethods.slice(0, COLLAPSE_THRESHOLD)
                          ).map(pm => (
                            <ListElement key={pm}>
                              <LuxTypography variant="textMdRegular">
                                {pm}
                              </LuxTypography>
                            </ListElement>
                          ))}
                        </ElementsList>
                        {payMethods.length > COLLAPSE_THRESHOLD && (
                          <LuxButton
                            variant="link"
                            onClick={() => setShowAllPayments(prev => !prev)}
                          >
                            {showAllPayments
                              ? t('moreInfosModal.showLess')
                              : t('moreInfosModal.showAll')}
                          </LuxButton>
                        )}
                      </MoreInfoAccordionContent>
                    </LuxAccordion>
                  )}
                {practiceInformation?.showAcceptedInsurances &&
                  acceptedInsurances &&
                  acceptedInsurances.length > 0 && (
                    <LuxAccordion
                      title={
                        <LuxTypography
                          variant="heading4"
                          component="h3"
                          padding={'0.5rem 0'}
                        >
                          {brandName
                            ? t('moreInfosModal.acceptedInsurances')
                            : t('moreInfosModal.acceptedInsurances2')}
                        </LuxTypography>
                      }
                      fullWidthContent
                      defaultExpanded
                    >
                      <MoreInfoAccordionContent>
                        <ElementsList isMobile={breakpoint === 'mobile'}>
                          {(showAllInsurances
                            ? acceptedInsurances
                            : acceptedInsurances.slice(0, COLLAPSE_THRESHOLD)
                          )
                            .filter(ins => ins.name !== 'Other')
                            .map(ins => (
                              <ListElement key={ins.insuranceId}>
                                <LuxTypography variant="textMdRegular">
                                  {ins.name}
                                </LuxTypography>
                              </ListElement>
                            ))}
                        </ElementsList>
                        {acceptedInsurances.filter(ins => ins.name !== 'Other')
                          .length > COLLAPSE_THRESHOLD && (
                          <LuxButton
                            variant="link"
                            onClick={() => setShowAllInsurances(prev => !prev)}
                          >
                            {showAllInsurances
                              ? t('moreInfosModal.showLess')
                              : t('moreInfosModal.showAll')}
                          </LuxButton>
                        )}
                      </MoreInfoAccordionContent>
                    </LuxAccordion>
                  )}
                {practiceInformation?.showFees && fees && (
                  <LuxAccordion
                    title={
                      <LuxTypography variant="heading4" padding={'0.5rem 0'}>
                        {t('moreInfosModal.fees')}
                      </LuxTypography>
                    }
                    fullWidthContent
                    defaultExpanded
                  >
                    <MoreInfoAccordionContent>
                      <LuxTypography variant="textMdRegular">
                        {fees}
                      </LuxTypography>
                    </MoreInfoAccordionContent>
                  </LuxAccordion>
                )}
              </>
            )}

            {/* ── Doctors tab ───────────────────────────────────────────── */}
            {selectedTab?.id === 'doctors' &&
              doctors &&
              doctors?.map(doc => {
                const visibleLanguages =
                  doc.items?.languages?.filter(language =>
                    Boolean(language?.trim()),
                  ) ?? [];

                const visibleSpecialties =
                  doc.items?.specialty?.filter(spec => Boolean(spec?.trim())) ??
                  [];

                const visibleEducation =
                  doc.items?.education?.filter(edu => Boolean(edu?.trim())) ??
                  [];

                const showLanguages = visibleLanguages.length > 0;
                const showGender =
                  doc.items?.gender === 'male' ||
                  doc.items?.gender === 'female';
                const showBiography = Boolean(doc.items?.biography?.trim());
                const showSpecialty = visibleSpecialties.length > 0;
                const showEducation = visibleEducation.length > 0;
                const showCustomMessage = Boolean(doc.customMessage?.trim());

                // stessa logica fallback richiesta:
                // se nessuno di questi campi è disponibile, mostro il messaggio placeholder
                const hasAnyConfiguredInfoField =
                  showLanguages ||
                  showGender ||
                  showBiography ||
                  showSpecialty ||
                  showEducation ||
                  showCustomMessage;

                return (
                  <LuxAccordion
                    title={
                      <LuxTypography
                        variant="heading4"
                        component="h3"
                        padding={'0.5rem 0'}
                      >
                        {doc.jobTitle} {doc.title}
                      </LuxTypography>
                    }
                    key={doc.doctorId}
                    fullWidthContent
                    defaultExpanded
                  >
                    <MoreInfoAccordionContent>
                      <DoctorAvatarInfoContainer>
                        {doc.avatar ? (
                          <img
                            src={doc?.avatar}
                            alt={doc?.title}
                            style={{
                              width: breakpoint === 'mobile' ? '100%' : '9rem',
                              height: '9rem',
                              borderRadius: '0.5rem',
                              objectFit:
                                breakpoint === 'mobile' ? 'contain' : 'cover',
                            }}
                          />
                        ) : (
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor:
                                theme.palette.LUX.obTokens.actionColorBgBrand,
                              borderRadius: '0.5rem',
                              width: `${breakpoint === 'mobile' ? '100%' : '9rem'}`,
                              height: '9rem',
                            }}
                          >
                            <UserDoctorIcon
                              width="7rem"
                              height="7rem"
                              color="white"
                            />
                          </div>
                        )}

                        <DoctorInfoContainer>
                          {showLanguages && (
                            <InfoRowContainer>
                              <LuxTypography
                                color={
                                  theme.palette.LUX.obTokens
                                    .textColorBaseDefault
                                }
                              >
                                {t('moreInfosModal.doctorData.lang')}
                              </LuxTypography>
                              <LuxTypography>
                                {visibleLanguages.join(', ')}
                              </LuxTypography>
                            </InfoRowContainer>
                          )}

                          {showGender && (
                            <InfoRowContainer>
                              <LuxTypography
                                color={
                                  theme.palette.LUX.obTokens
                                    .textColorBaseDefault
                                }
                              >
                                {t('moreInfosModal.doctorData.gender')}
                              </LuxTypography>
                              <LuxTypography>{doc.items?.gender}</LuxTypography>
                            </InfoRowContainer>
                          )}

                          {showBiography && (
                            <InfoRowContainer>
                              <LuxTypography
                                color={
                                  theme.palette.LUX.obTokens
                                    .textColorBaseDefault
                                }
                              >
                                {t('moreInfosModal.doctorData.bio')}
                              </LuxTypography>
                              <LuxTypography>
                                {doc.items?.biography}
                              </LuxTypography>
                            </InfoRowContainer>
                          )}

                          {showSpecialty && (
                            <InfoRowContainer>
                              <LuxTypography
                                color={
                                  theme.palette.LUX.obTokens
                                    .textColorBaseDefault
                                }
                              >
                                {t('moreInfosModal.doctorData.spec')}
                              </LuxTypography>
                              <LuxTypography>
                                {visibleSpecialties.join(', ')}
                              </LuxTypography>
                            </InfoRowContainer>
                          )}

                          {showEducation && (
                            <InfoRowContainer>
                              <LuxTypography
                                color={
                                  theme.palette.LUX.obTokens
                                    .textColorBaseDefault
                                }
                              >
                                {t('moreInfosModal.doctorData.education')}
                              </LuxTypography>
                              <LuxTypography>
                                {visibleEducation.join(', ')}
                              </LuxTypography>
                            </InfoRowContainer>
                          )}
                        </DoctorInfoContainer>
                      </DoctorAvatarInfoContainer>
                      {!hasAnyConfiguredInfoField && (
                        <LuxTypography
                          variant="textMdRegular"
                          color={
                            theme.palette.LUX.obTokens.textColorBaseSecondary
                          }
                        >
                          {t('practiceCard.ourDoctorsPlaceholder')}
                        </LuxTypography>
                      )}
                      {showCustomMessage && (
                        <CustMessContainer>
                          <LuxTypography variant="textMedium">
                            {t('moreInfosModal.doctorData.custMess')}
                          </LuxTypography>
                          <CustomMessageTextBox
                            role="group"
                            aria-label={`${t('moreInfosModal.doctorData.custMess')}: ${doc.customMessage}`}
                          >
                            <CustomMessageText aria-hidden="true">
                              {doc.customMessage}
                            </CustomMessageText>
                          </CustomMessageTextBox>
                        </CustMessContainer>
                      )}
                    </MoreInfoAccordionContent>
                  </LuxAccordion>
                );
              })}
          </ModalContentWrapper>
        </LuxModal>,
        document.body,
      )
    : null;
};

export default MoreInformationModal;
